import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FiChevronLeft, FiChevronRight, FiPlay } from "react-icons/fi";

function isVideoItem(item) {
  if (item?.type) {
    return item.type === "video";
  }
  const src = item?.src || "";
  return /\.(mp4|webm|ogg|mov)$/i.test(src);
}

function normalizeItems(items) {
  return items
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          id: `${item}-${index}`,
          src: item,
          alt: `slide-${index + 1}`,
          type: /\.(mp4|webm|ogg|mov)$/i.test(item) ? "video" : "image",
        };
      }
      return {
        id: item.id || `${item.src}-${index}`,
        src: item.src,
        thumbnail: item.thumbnail || item.poster || item.src,
        poster: item.poster,
        alt: item.alt || `slide-${index + 1}`,
        type: item.type || (isVideoItem(item) ? "video" : "image"),
      };
    })
    .filter((item) => Boolean(item.src));
}

function VideoThumbFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-slate-700 text-white">
      <FiPlay className="h-4 w-4" aria-hidden />
    </div>
  );
}

export default function AppCarousel({
  items,
  initialIndex = 0,
  onSlideChange,
  autoPlay = false,
  autoPlayInterval = 4000,
  className = "",
  heightClassName = "h-[320px] md:h-[420px]",
}) {
  const mediaItems = useMemo(() => normalizeItems(items), [items]);
  const safeInitialIndex = Math.min(Math.max(initialIndex, 0), Math.max(mediaItems.length - 1, 0));
  const [activeIndex, setActiveIndex] = useState(safeInitialIndex);

  const setSlide = useCallback(
    (nextIndex) => {
      if (!mediaItems.length) return;
      const wrappedIndex = (nextIndex + mediaItems.length) % mediaItems.length;
      setActiveIndex(wrappedIndex);
      onSlideChange?.(wrappedIndex, mediaItems[wrappedIndex]);
    },
    [mediaItems, onSlideChange],
  );

  const hasMultiple = mediaItems.length > 1;

  useEffect(() => {
    if (!autoPlay || !hasMultiple) return undefined;
    const timer = window.setInterval(() => {
      setSlide(activeIndex + 1);
    }, autoPlayInterval);
    return () => window.clearInterval(timer);
  }, [autoPlay, hasMultiple, activeIndex, autoPlayInterval, setSlide]);

  if (!mediaItems.length) {
    return (
      <div className={`rounded-2xl border border-slate-200 bg-slate-100 p-8 text-center text-sm text-text-500 ${className}`}>
        No media available.
      </div>
    );
  }

  const active = mediaItems[activeIndex];

  return (
    <section className={`relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 shadow-card ${className}`}>
      <div className={`relative w-full ${heightClassName}`}>
        {active.type === "video" ? (
          <video
            key={active.src}
            className="h-full w-full object-cover"
            controls
            preload="metadata"
            poster={active.poster || active.thumbnail}
          >
            <source src={active.src} />
            Your browser does not support the video tag.
          </video>
        ) : (
          <img src={active.src} alt={active.alt} className="h-full w-full object-cover" />
        )}

        {hasMultiple ? (
          <>
            <button
              type="button"
              onClick={() => setSlide(activeIndex - 1)}
              className="absolute left-4 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-slate-700/70 text-white backdrop-blur-sm transition hover:bg-slate-700"
              aria-label="Previous slide"
            >
              <FiChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setSlide(activeIndex + 1)}
              className="absolute right-4 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-slate-700/70 text-white backdrop-blur-sm transition hover:bg-slate-700"
              aria-label="Next slide"
            >
              <FiChevronRight className="h-5 w-5" />
            </button>
          </>
        ) : null}
      </div>

      {hasMultiple ? (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent px-4 pb-4 pt-12">
          <div className="mx-auto flex max-w-3xl items-center justify-center gap-3 overflow-x-auto">
            {mediaItems.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSlide(index)}
                  className={`relative h-16 w-28 shrink-0 overflow-hidden rounded-xl border transition ${
                    isActive
                      ? "border-brand-500 ring-2 ring-brand-300"
                      : "border-white/40 hover:border-white/70"
                  }`}
                  aria-label={`Open slide ${index + 1}`}
                  aria-current={isActive ? "true" : "false"}
                >
                  {item.type === "video" ? (
                    <>
                      {item.thumbnail ? (
                        <img src={item.thumbnail} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <VideoThumbFallback />
                      )}
                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25 text-white">
                        <FiPlay className="h-4 w-4" />
                      </span>
                    </>
                  ) : (
                    <img src={item.thumbnail || item.src} alt="" className="h-full w-full object-cover" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}

AppCarousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        src: PropTypes.string.isRequired,
        thumbnail: PropTypes.string,
        poster: PropTypes.string,
        alt: PropTypes.string,
        type: PropTypes.oneOf(["image", "video"]),
      }),
    ]),
  ).isRequired,
  initialIndex: PropTypes.number,
  onSlideChange: PropTypes.func,
  autoPlay: PropTypes.bool,
  autoPlayInterval: PropTypes.number,
  className: PropTypes.string,
  heightClassName: PropTypes.string,
};
